import React, { useEffect, useMemo, useState } from "react";
import { cqAPI } from "../../lib/Auth";
import {
  Box,
  Flex,
  Text,
  VStack,
  HStack,
  Checkbox,
  useTheme,
  useColorMode,
  Button,
  Badge,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  Spinner,
  ModalCloseButton,
  ModalBody,
  IconButton,
  ModalFooter,
  useDisclosure,
  FormControl,
  FormLabel,
  Input,
  Menu,
  MenuItem,
  MenuButton,
  useToast,
  MenuList,
} from "@chakra-ui/react";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEllipsisV } from "@fortawesome/free-solid-svg-icons";

import { AddIcon } from "@chakra-ui/icons";

import dialogManager from "../DialogManager/DialogManager";

const HEADER_ERROR = "Error";

const EMPTY_EDITOR = { role_id: null, name: "", description: "", permissions: [] };

function RoleManagement() {
  const theme = useTheme();
  const { colorMode } = useColorMode();
  const toast = useToast();

  const [roleData, setRoleData] = useState({
    system: [],
    custom: [],
    permissions: [],
  });
  const [isLoading, setIsLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [editor, setEditor] = useState(EMPTY_EDITOR);

  const { isOpen: isEditorOpen, onOpen: onEditorOpen, onClose } = useDisclosure();

  const hoverBgColor =
    colorMode === "light" ? theme.colors.gray[200] : theme.colors.gray[700];
  const dividerColor = colorMode === "light" ? "gray.200" : "gray.700";
  const mutedColor = colorMode === "light" ? "gray.500" : "gray.400";

  const fetchRoles = async () => {
    const response = await cqAPI.get(`/settings/roles`);
    setIsLoading(false);
    if (!response || response.type === "error") {
      setLoadFailed(true);
      if (response?.msg) {
        dialogManager.open({
          onDeny: () => {},
          acceptText: "OK",
          body: response.msg,
          header: HEADER_ERROR,
        });
      }
      return;
    }
    setLoadFailed(false);
    setRoleData(response.data);
  };

  useEffect(() => {
    fetchRoles();
  }, []);

  // The permission catalog is flat with a `parent` field; the editor renders it
  // as a tree and keeps the hierarchy rule: enabling a permission enables its
  // ancestors, disabling one disables all of its descendants. Standalone
  // permissions (the per-report ones) are exempt — they group under their
  // parent visually but are independent grants in both directions.
  const { permissionTree, parentOf, childrenOf, editableIds, standaloneIds } = useMemo(() => {
    const byId = {};
    const roots = [];
    const parents = {};
    const children = {};
    const standalone = new Set();
    (roleData.permissions || []).forEach((p) => {
      byId[p.id] = { ...p, children: [] };
      if (p.standalone) {
        standalone.add(p.id);
      }
    });
    (roleData.permissions || []).forEach((p) => {
      if (p.parent && byId[p.parent]) {
        byId[p.parent].children.push(byId[p.id]);
        parents[p.id] = p.parent;
        children[p.parent] = children[p.parent] || [];
        children[p.parent].push(p.id);
      } else {
        roots.push(byId[p.id]);
      }
    });
    return {
      permissionTree: roots,
      parentOf: parents,
      childrenOf: children,
      editableIds: new Set(Object.keys(byId)),
      standaloneIds: standalone,
    };
  }, [roleData.permissions]);

  const togglePermission = (id, checked) => {
    const next = new Set(editor.permissions);
    if (checked) {
      next.add(id);
      if (!standaloneIds.has(id)) {
        let current = parentOf[id];
        while (current && !next.has(current)) {
          next.add(current);
          current = parentOf[current];
        }
      }
    } else {
      const removeWithDescendants = (permissionId) => {
        next.delete(permissionId);
        (childrenOf[permissionId] || [])
          .filter((child) => !standaloneIds.has(child))
          .forEach(removeWithDescendants);
      };
      removeWithDescendants(id);
    }
    setEditor({ ...editor, permissions: [...next] });
  };

  const openCreate = () => {
    setEditor(EMPTY_EDITOR);
    onEditorOpen();
  };

  const openEdit = (role) => {
    setEditor({
      role_id: role.role,
      name: role.name || "",
      description: role.description || "",
      permissions: (role.permissions || []).filter((p) => editableIds.has(p)),
    });
    onEditorOpen();
  };

  const openDuplicate = (role) => {
    setEditor({
      role_id: null,
      name: "",
      description: role.description || "",
      permissions: (role.permissions || []).filter((p) => editableIds.has(p)),
    });
    onEditorOpen();
  };

  const handleSave = async () => {
    if (!editor.name.trim()) {
      toast({
        title: "Not a valid value",
        description: "Please enter a name for the role.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }
    if (!editor.permissions.length) {
      toast({
        title: "Not a valid value",
        description: "Please select at least one permission.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    setIsSaving(true);
    const response = await cqAPI.post(`/settings/roles`, {
      ...(editor.role_id ? { role_id: editor.role_id } : {}),
      name: editor.name.trim(),
      description: editor.description,
      permissions: editor.permissions,
    });
    setIsSaving(false);

    if (!response) return; // the user declined the confirmation dialog

    if (response.type === "error") {
      if (response.msg) {
        dialogManager.open({
          onDeny: () => {},
          acceptText: "OK",
          body: response.msg,
          header: HEADER_ERROR,
        });
      }
      return;
    }

    onClose();
    toast({
      title: "Success",
      description: editor.role_id ? "The role was updated." : "The role was created.",
      status: "success",
      duration: 5000,
      isClosable: true,
    });
    await fetchRoles();
  };

  const handleDelete = async (role) => {
    const response = await cqAPI.del(`/settings/roles?role_id=${role.role}`);

    if (!response) return; // the user declined the confirmation dialog

    if (response.type === "error") {
      if (response.msg) {
        dialogManager.open({
          onDeny: () => {},
          acceptText: "OK",
          body: response.msg,
          header: HEADER_ERROR,
        });
      }
      return;
    }

    toast({
      title: "Success",
      description: "The role was removed.",
      status: "success",
      duration: 5000,
      isClosable: true,
    });
    await fetchRoles();
  };

  const renderPermissionNode = (node, depth = 0) => (
    // children render inside the parent's Box, so a constant per-level padding
    // accumulates through nesting on its own
    <Box key={node.id} pl={depth ? 6 : 0}>
      <Checkbox
        isChecked={editor.permissions.indexOf(node.id) !== -1}
        onChange={(e) => togglePermission(node.id, e.target.checked)}
        alignItems="flex-start"
        pt={1}
      >
        <Text fontSize="sm" textAlign="left">{node.name}</Text>
        {node.description && (
          <Text fontSize="xs" color={mutedColor} textAlign="left">
            {node.description}
          </Text>
        )}
      </Checkbox>
      {node.children.map((child) => renderPermissionNode(child, depth + 1))}
    </Box>
  );

  const editorModal = (
    <Modal isOpen={isEditorOpen} onClose={onClose} size="lg" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader textAlign="left">
          {editor.role_id ? "Edit role" : "Create a new role"}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4} align="stretch">
            <FormControl isRequired>
              <FormLabel textAlign="left">Name</FormLabel>
              <Input
                placeholder="e.g. Regional Manager"
                maxLength={80}
                value={editor.name}
                onChange={(e) => setEditor({ ...editor, name: e.target.value })}
              />
            </FormControl>
            <FormControl>
              <FormLabel textAlign="left">Description</FormLabel>
              <Input
                placeholder="What is this role for?"
                value={editor.description}
                onChange={(e) => setEditor({ ...editor, description: e.target.value })}
              />
            </FormControl>
            <FormControl>
              <FormLabel textAlign="left">Permissions</FormLabel>
              <Text fontSize="xs" color={mutedColor} textAlign="left" mb={2}>
                People with this role get the selected permissions at every
                location the role is assigned to them for.
              </Text>
              <VStack
                align="stretch"
                spacing={1}
                borderWidth="1px"
                borderColor={dividerColor}
                borderRadius="md"
                p={3}
                maxHeight="320px"
                overflowY="auto"
              >
                {permissionTree.map((node) => renderPermissionNode(node))}
              </VStack>
            </FormControl>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button colorScheme="blue" mr={3} onClick={handleSave} isDisabled={isSaving}>
            Save
            {isSaving && <Spinner size="sm" ml="2" />}
          </Button>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );

  const renderRoleRow = (role, isSystem) => {
    // count only the permissions the editor actually offers, so the number
    // matches what "Use as a template" / Edit will show
    const permissionCount = (role.permissions || []).filter((p) => editableIds.has(p)).length;
    return (
    <Flex
      key={role.role}
      borderBottom="1px"
      borderColor={dividerColor}
      p={2}
      _hover={{ bg: hoverBgColor }}
    >
      <HStack spacing={8} flex="1">
        <Flex direction="column" justifyContent="left" flexBasis="25%">
          <HStack>
            <Text fontSize="sm" fontWeight="bold" textAlign="left">
              {role.name}
            </Text>
            {isSystem && <Badge>System</Badge>}
          </HStack>
        </Flex>
        <Text fontSize="sm" flexBasis="45%" textAlign="left" color={mutedColor}>
          {role.description}
        </Text>
        <Text fontSize="sm" flexBasis="20%" textAlign="left">
          {permissionCount} permission{permissionCount === 1 ? "" : "s"}
          {!isSystem && `, ${role.assigned || 0} ${(role.assigned || 0) === 1 ? "person" : "people"}`}
        </Text>
        <Box flexBasis="5%">
          <Menu>
            <MenuButton
              as={IconButton}
              aria-label="Role actions"
              icon={<FontAwesomeIcon icon={faEllipsisV} />}
              backgroundColor={hoverBgColor}
              color={theme.colors.iconColor}
            />
            <MenuList>
              {isSystem ? (
                <MenuItem onClick={() => openDuplicate(role)}>
                  Use as a template for a new role
                </MenuItem>
              ) : (
                <>
                  <MenuItem onClick={() => openEdit(role)}>Edit</MenuItem>
                  <MenuItem onClick={() => openDuplicate(role)}>Duplicate</MenuItem>
                  <MenuItem onClick={() => handleDelete(role)}>Delete</MenuItem>
                </>
              )}
            </MenuList>
          </Menu>
        </Box>
      </HStack>
    </Flex>
    );
  };

  if (isLoading) {
    return (
      <Flex p={10} justifyContent="center" width="100%">
        <Spinner />
      </Flex>
    );
  }

  if (loadFailed) {
    return (
      <Flex p={10} direction="column" alignItems="center" width="100%" gap={4}>
        <Text fontSize="sm" color={mutedColor}>
          The roles could not be loaded. Please try again.
        </Text>
        <Button onClick={() => { setIsLoading(true); fetchRoles(); }}>Retry</Button>
      </Flex>
    );
  }

  return (
    <Box p={4} overflowY="auto" height="100%" width="100%">
      {editorModal}
      <Flex justifyContent="flex-end" mb={4}>
        <Button leftIcon={<AddIcon />} onClick={openCreate}>
          Add Role
        </Button>
      </Flex>
      <VStack spacing={4} align="stretch" mb={40}>
        <Text fontSize="md" fontWeight="bold" textAlign="left">
          Custom roles
        </Text>
        {!roleData.custom.length && (
          <Text fontSize="sm" color={mutedColor} textAlign="left">
            No custom roles yet. Create one to give your team exactly the
            access they need, or start from a system role below.
          </Text>
        )}
        {roleData.custom.map((role) => renderRoleRow(role, false))}

        <Text fontSize="md" fontWeight="bold" textAlign="left" pt={6}>
          System roles
        </Text>
        <Text fontSize="sm" color={mutedColor} textAlign="left">
          Built-in roles that cannot be changed. Use one as a template to
          create a custom role.
        </Text>
        {roleData.system.map((role) => renderRoleRow(role, true))}
      </VStack>
    </Box>
  );
}

export default RoleManagement;
